// src/components/PWARebrandBanner.js
//
// One-time banner shown only to users running in PWA standalone mode who
// installed under the old FamilyVault brand. Explains the rebrand and
// gives platform-specific steps to refresh their home screen icon/name.
// Dismissed state is stored in localStorage so it never reappears.

import { useEffect, useState } from "react";

const STORAGE_KEY = "pwa_rebrand_noticed_v1";

function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function PWARebrandBanner() {
  const [visible, setVisible]   = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    if (!isStandalone()) return;
    setVisible(true);
  }, []);

  const handleDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) {}
    setVisible(false);
  };

  if (!visible) return null;

  const ios = isIOS();

  const steps = ios
    ? [
        "Tap the Share button (□↑) at the bottom of Safari",
        'Tap "Remove from Home Screen" to delete the old shortcut',
        'Open the app in Safari, tap Share again, then "Add to Home Screen"',
      ]
    : [
        "Tap the three-dot menu (⋮) in Chrome",
        'Tap "Add to Home Screen" to replace the old shortcut',
        "The new Marelle Memories icon will appear on your home screen",
      ];

  return (
    <div
      role="dialog"
      aria-label="App rebrand notice"
      style={{
        position: "fixed",
        bottom: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 49,
        width: "calc(100vw - 32px)",
        maxWidth: 400,
        background: "var(--card)",
        border: "1px solid var(--border)",
        borderRadius: 18,
        boxShadow: "0 12px 40px rgba(0,0,0,0.28)",
        padding: "16px 18px",
        color: "var(--text)",
        fontSize: "0.88rem",
      }}
    >
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: "1.6rem", flexShrink: 0, lineHeight: 1 }}>🎉</span>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: "0.95rem", color: "var(--text)" }}>
            We have a new name!
          </p>
          <p style={{ margin: "3px 0 0", opacity: 0.7, lineHeight: 1.4 }}>
            FamilyVault is now{" "}
            <strong style={{ color: "var(--accent)", opacity: 1 }}>Marelle Memories</strong>.
            Update your home screen icon to get the new look.
          </p>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text)", opacity: 0.45, fontSize: "1rem",
            padding: "0 2px", lineHeight: 1, flexShrink: 0,
          }}
        >
          ✕
        </button>
      </div>

      {/* Expandable steps */}
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          background: "none", border: "none", cursor: "pointer",
          color: "var(--accent)", fontWeight: 600, fontSize: "0.82rem",
          padding: "2px 0", display: "flex", alignItems: "center", gap: 4,
        }}
      >
        {expanded ? "Hide steps ▲" : "How to update ▼"}
      </button>

      {expanded && (
        <ol style={{ margin: "10px 0 12px", paddingLeft: 18, lineHeight: 1.6, opacity: 0.85 }}>
          {steps.map((s, i) => (
            <li key={i} style={{ marginBottom: 4 }}>{s}</li>
          ))}
        </ol>
      )}

      {/* Dismiss button */}
      <div style={{ marginTop: expanded ? 0 : 12 }}>
        <button
          onClick={handleDismiss}
          style={{
            width: "100%", padding: "9px 0", borderRadius: 12,
            border: "1px solid var(--border)", background: "var(--muted)",
            color: "var(--text)", fontWeight: 500, fontSize: "0.83rem", cursor: "pointer",
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
